import React, { useState } from 'react'
import IconButton from '@mui/material/IconButton'
import Badge from '@mui/material/Badge'
import Popover from '@mui/material/Popover'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import Divider from '@mui/material/Divider'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import NotificationsIcon from '@mui/icons-material/Notifications'
import AssignmentIcon from '@mui/icons-material/Assignment'
import RateReviewIcon from '@mui/icons-material/RateReview'
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer'
import ChatIcon from '@mui/icons-material/Chat'
import WebhookIcon from '@mui/icons-material/Webhook'
import CloseIcon from '@mui/icons-material/Close'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import { router } from '@inertiajs/react'
import { useNotificationCenter, AppNotification } from '@/hooks/useNotificationCenter'

function getNotificationIcon(type: string) {
  switch (type) {
    case 'task_assigned':
    case 'task_status_changed':
      return <AssignmentIcon fontSize="small" />
    case 'review_requested':
      return <RateReviewIcon fontSize="small" />
    case 'clarification_received':
      return <QuestionAnswerIcon fontSize="small" />
    case 'message_received':
      return <ChatIcon fontSize="small" />
    case 'webhook_event':
      return <WebhookIcon fontSize="small" />
    default:
      return <NotificationsIcon fontSize="small" />
  }
}

function getTypeLabel(type: string): string {
  switch (type) {
    case 'task_assigned': return 'Task'
    case 'task_status_changed': return 'Status'
    case 'review_requested': return 'Review'
    case 'clarification_received': return 'Clarification'
    case 'message_received': return 'Message'
    case 'session_completed': return 'Session'
    case 'session_failed': return 'Session'
    case 'webhook_event': return 'Webhook'
    default: return 'Notification'
  }
}

function getTypeColor(type: string): 'primary' | 'secondary' | 'warning' | 'error' | 'info' | 'success' {
  switch (type) {
    case 'task_assigned': return 'primary'
    case 'review_requested': return 'secondary'
    case 'clarification_received': return 'warning'
    case 'message_received': return 'info'
    case 'session_failed': return 'error'
    case 'session_completed': return 'success'
    default: return 'primary'
  }
}

function timeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

export function NotificationCenter() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const {
    notifications,
    unreadCount,
    loading,
    markAllRead,
    markRead,
    dismissNotification,
  } = useNotificationCenter()

  const open = Boolean(anchorEl)

  function handleOpen(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget)
  }

  function handleClose() {
    setAnchorEl(null)
  }

  function handleClick(notification: AppNotification) {
    if (!notification.read) {
      markRead(notification.id)
    }
    if (notification.url) {
      handleClose()
      router.visit(notification.url)
    }
  }

  function handleDismiss(e: React.MouseEvent, id: number) {
    e.stopPropagation()
    dismissNotification(id)
  }

  function handleMarkAllRead() {
    markAllRead()
  }

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        sx={{ color: 'text.secondary', mr: 0.5 }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              height: 16,
              minWidth: 16,
            },
          }}
        >
          <NotificationsIcon sx={{ fontSize: 20 }} />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: '100vw', sm: 380 },
              maxHeight: 480,
              mt: 1,
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllRead}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {/* Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ py: 0, maxHeight: 400, overflowY: 'auto' }}>
            {notifications.map((notification) => (
              <ListItemButton
                key={notification.id}
                onClick={() => handleClick(notification)}
                sx={{
                  backgroundColor: notification.read ? 'transparent' : 'action.hover',
                  alignItems: 'flex-start',
                  py: 1.5,
                  px: 2,
                  '&:hover .dismiss-btn': { opacity: 1 },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, mt: 0.5, color: notification.read ? 'text.disabled' : 'primary.main' }}>
                  {getNotificationIcon(notification.notification_type)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                      <Typography
                        variant="body2"
                        fontWeight={notification.read ? 400 : 600}
                        sx={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {notification.title}
                      </Typography>
                      <Chip
                        label={getTypeLabel(notification.notification_type)}
                        size="small"
                        color={getTypeColor(notification.notification_type)}
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.6rem', flexShrink: 0 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      {notification.body && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {notification.body}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                        {timeAgo(notification.created_at)}
                      </Typography>
                    </Box>
                  }
                />
                <IconButton
                  className="dismiss-btn"
                  size="small"
                  onClick={(e) => handleDismiss(e, notification.id)}
                  sx={{ opacity: 0, transition: 'opacity 0.2s', ml: 0.5, mt: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>
    </>
  )
}
